/* begin general import */
import {
  Add16,
  OverflowMenuHorizontal24,
  SettingsAdjust16,
  TrashCan16,
} from "@carbon/icons-react";
import { Dropdown, Menu, PaginationProps, Spin } from "antd";
import { ColumnProps } from "antd/lib/table";
import PageHeader from "components/PageHeader/PageHeader";
import { SUBJECT_ROUTE } from "config/route-consts";
import { Cohort, CohortFilter } from "models/Cohort";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionBarComponent,
  Button,
  OneLineText,
  Pagination,
  StandardTable,
  TagFilter,
} from "react3l-ui-library";
import { detailService } from "services/page-services/detail-service";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { masterService } from "services/page-services/master-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";
import CohortDetailDrawer from "../CohortDetail/CohortDetailDrawer";
import CohortAdvanceFilter from "./CohortAdvanceFilter";
import { cohortRepository } from "repositories/cohort-repository";
import { renderMasterIndex } from "helpers/table";
import InputSearch from "components/InputSearch";
import "./CohortMaster.scss";
import { Moment } from "moment";
import { formatDate } from "helpers/date-time";
import LayoutHeader from "components/LayoutHeader";
import LayoutCell from "components/LayoutCell";
import { formatNumber } from "helpers/number";
import TruncateCell from "components/TruncateCell";
import AdvanceDateRangeFilterMaster from "components/AdvanceDateRangeFilter";
/* end individual import */

function CohortMaster() {
  const [translate] = useTranslation();
  const [modelFilter, dispatch] = queryStringService.useQueryString(
    CohortFilter,
    { skip: 0, take: 10 }
  );

  const {
    value: filter,
    handleChangeAllFilter,
    handleChangeDateMasterFilter,
    handleChangeInputSearch,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList, handleResetList, handleLoadList } =
    listService.useList(
      cohortRepository.list,
      cohortRepository.count,
      filter,
      dispatch
    );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const {
    handleAction,
    handleBulkAction,
    canBulkAction,
    selectedRowKeys,
    rowSelection,
    setSelectedRowKeys,
  } = listService.useRowSelection(
    cohortRepository.delete,
    cohortRepository.bulkDelete,
    null,
    null,
    null,
    handleResetList
  );

  const { handleDeleteItem } = masterService.useMasterAction(
    SUBJECT_ROUTE,
    handleAction
  );

  const {
    model,
    dispatch: dispatchModal,
    isOpenDetailModal,
    handleOpenDetailModal,
    handleCloseDetailModal,
    handleSaveModel,
    loadingModel,
    handleChangeSingleField,
    handleChangeDateField,
  } = detailService.useDetailModal(
    Cohort,
    cohortRepository.get,
    cohortRepository.save,
    handleLoadList
  );

  const [visible, setVisible] = React.useState<boolean>(false);

  const menuAction = React.useCallback(
    (id: number, cohort: Cohort) => (
      <Menu>
        {
          <Menu.Item key="1">
            <div
              className="ant-action-menu text-center"
              onClick={() => handleOpenDetailModal(id)}
            >
              {translate("general.actions.edit")}
            </div>
          </Menu.Item>
        }

        {!cohort?.used && (
          <Menu.Item key="2">
            <div
              className="ant-action-menu text-center"
              onClick={handleDeleteItem(cohort)}
            >
              {translate("general.actions.delete")}
            </div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [translate, handleDeleteItem, handleOpenDetailModal]
  );

  const pagination: PaginationProps = tableService.usePagination(filter, count);

  const columns: ColumnProps<Cohort>[] = useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
            position="center"
          />
        ),
        key: "index",
        width: 60,
        render(...params: [number, Cohort, number]) {
          const index = renderMasterIndex<Cohort>(pagination)(...params);
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={formatNumber(index)} />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("cohorts.code")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].code),
        dataIndex: nameof(list[0].code),
        sorter: true,
        sortOrder: getAntOrderType<Cohort, CohortFilter>(
          filter,
          nameof(list[0].code)
        ),
        width: "30%",
        render(...params: [string, Cohort, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "name"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("cohorts.name")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        sortOrder: getAntOrderType<Cohort, CohortFilter>(
          filter,
          nameof(list[0].name)
        ),
        render(...params: [string, Cohort, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "startedAt"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("cohorts.startedAt")}
              sortedColumn={sortedColumn}
              isSorter
              position="center"
            />
          );
        },
        key: nameof(list[0].startedAt),
        dataIndex: nameof(list[0].startedAt),
        sorter: true,
        sortOrder: getAntOrderType<Cohort, CohortFilter>(
          filter,
          nameof(list[0].startedAt)
        ),
        width: 200,
        render(...params: [Moment, Cohort, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0] ? formatDate(params[0]) : ""} />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "endedAt"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("cohorts.endedAt")}
              sortedColumn={sortedColumn}
              isSorter
              position="center"
            />
          );
        },
        key: nameof(list[0].endedAt),
        dataIndex: nameof(list[0].endedAt),
        sorter: true,
        sortOrder: getAntOrderType<Cohort, CohortFilter>(
          filter,
          nameof(list[0].endedAt)
        ),
        width: 200,
        render(...params: [Moment, Cohort, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0] ? formatDate(params[0]) : ""} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("general.actions.label")}
            />
          );
        },
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, cohort: Cohort) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Dropdown
                overlay={menuAction(id, cohort)}
                trigger={["click"]}
                placement="bottom"
                arrow
              >
                <OverflowMenuHorizontal24 />
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [translate, pagination, list, filter, menuAction]
  );

  const handleCancel = React.useCallback(() => {
    setVisible(false);
  }, []);
  return (
    <>
      <Spin spinning={loadingList}>
        <div className="page-content cohort-master">
          <PageHeader
            title={translate("cohorts.master.subHeader")}
            breadcrumbItems={[
              translate("cohorts.master.header"),
              translate("cohorts.master.subHeader"),
            ]}
          />

          <div className="page page-master m-l--sm m-r--xxl m-b--xxs">
            <div className="page-master__title p-l--sm p-t--xs p-b--xs">
              {translate("cohorts.master.subHeader")}
            </div>
            <div className="page-master__content">
              <div className="page-master__tag-filter">
                <TagFilter
                  value={filter}
                  translate={translate}
                  keyTranslate={"cohorts"}
                  handleChangeFilter={handleChangeAllFilter}
                  onClear={() => {
                    return 0;
                  }}
                />
              </div>
              {(!selectedRowKeys || selectedRowKeys?.length === 0) && (
                <div className="page-master__filter-wrapper d-flex align-items-center justify-content-between">
                  <div className="page-master__filter d-flex align-items-center justify-content-start">
                    <div className="d-flex align-items-center">
                      <div className="">
                        <AdvanceDateRangeFilterMaster
                          value={[
                            modelFilter?.startedAt?.greaterEqual as any,
                            modelFilter?.startedAt?.lessEqual as any,
                          ]}
                          placeholder={[
                            translate("general.filter.startDate"),
                            translate("general.filter.endDate"),
                          ]}
                          onChange={handleChangeDateMasterFilter({
                            fieldName: nameof(list[0].startedAt),
                            fieldType: ["greaterEqual", "lessEqual"],
                          })}
                          label={translate("cohorts.startedAt")}
                          translate={translate}
                        />
                      </div>
                      <div className="">
                        <AdvanceDateRangeFilterMaster
                          value={[
                            modelFilter?.endedAt?.greaterEqual as any,
                            modelFilter?.endedAt?.lessEqual as any,
                          ]}
                          placeholder={[
                            translate("general.filter.startDate"),
                            translate("general.filter.endDate"),
                          ]}
                          onChange={handleChangeDateMasterFilter({
                            fieldName: nameof(list[0].endedAt),
                            fieldType: ["greaterEqual", "lessEqual"],
                          })}
                          label={translate("cohorts.endedAt")}
                          translate={translate}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="page-master__filter-action-search d-flex align-items-center">
                    <Button
                      type="icon-only-ghost"
                      icon={<SettingsAdjust16 />}
                      onClick={() => setVisible(true)}
                      className="btn--xl"
                    />
                    <InputSearch
                      value={filter?.search}
                      classFilter={CohortFilter}
                      placeHolder={translate("general.placeholder.search")}
                      onChange={handleChangeInputSearch}
                    />
                  </div>

                  <div className="page-master__actions  d-flex align-items-center justify-content-start">
                    <div className="page-master__filter-action d-flex align-items-center">
                      <Button
                        type="primary"
                        className="btn--lg"
                        icon={<Add16 />}
                        onClick={() => handleOpenDetailModal(null)}
                      >
                        {translate("general.actions.create")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="page-master__content-table">
              <ActionBarComponent
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
              >
                <Button
                  icon={<TrashCan16 />}
                  type="ghost-primary"
                  className="btn--lg"
                  disabled={!canBulkAction}
                  onClick={() => handleBulkAction(selectedRowKeys)}
                >
                  {translate("general.actions.delete")}
                </Button>
              </ActionBarComponent>
              <StandardTable
                rowKey={nameof(list[0].id)}
                columns={columns}
                dataSource={list}
                isDragable={true}
                tableSize={"md"}
                onChange={handleTableChange}
                rowSelection={rowSelection}
                spinning={loadingList}
                scroll={{ y: 500 }}
                bordered
              />

              <Pagination
                skip={filter?.skip}
                take={filter?.take}
                total={count}
                onChange={handlePagination}
                //canChangePageSize={false}
              />
            </div>
          </div>
        </div>
      </Spin>

      {visible && (
        <CohortAdvanceFilter
          visible={visible}
          handleClose={handleCancel}
          visibleFooter={true}
          loading={false}
          size={"sm"}
          filter={filter}
          setVisible={setVisible}
          handleChangeAllFilter={handleChangeAllFilter}
        />
      )}
      {isOpenDetailModal && (
        <CohortDetailDrawer
          model={model}
          visible={isOpenDetailModal}
          handleSave={handleSaveModel}
          handleCancel={handleCloseDetailModal}
          handleChangeSingleField={handleChangeSingleField}
          handleChangeDateField={handleChangeDateField}
          dispatch={dispatchModal}
          loading={loadingModel}
          visibleFooter={true}
        />
      )}
    </>
  );
}
export default CohortMaster;
